import { styled } from '@linaria/react';
import { Icon } from '@iconify/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { getUserAuthStartUrl } from '../../../api/google-integrations-client/google-integrations-client';
import { useGoogleStatus } from '../../../api/google-integrations-client/google-integrations-client.hooks';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { Button } from '../../../ui-library/button/button';
import { H2, Text } from '../../../ui-library/typography/typography';
import { useGooglePicker } from '../hooks/use-google-picker';
import { openOAuthPopup } from './agent-integrations/agent-integrations.helpers';
import { Hint, IntegrationsLink, INTEGRATIONS_PAGE_PATH } from './shared/sheet-access-hint';

/** Terminal/interstitial states for the grant-access flow. */
type GrantAccessStatus = 'idle' | 'opening-picker' | 'granted' | 'picked-different' | 'reconnect-required' | 'error';

/**
 * Standalone page for granting Flora access to a specific Google Sheet, reached
 * via a deep link Flora emits (e.g. in Slack) when a user references a file it
 * cannot access. Reads the file id from the query string, connects Google if
 * needed, then opens the Google Picker so the user can grant access without
 * pasting the link into the web chat.
 *
 * @returns {JSX.Element} The grant-access page.
 */
export function GrantAccessView() {
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get('fileId');
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const { data: googleStatus, query } = useGoogleStatus();
  const { openPickerForFile } = useGooglePicker();

  const [status, setStatus] = useState<GrantAccessStatus>('idle');
  const [isConnecting, setIsConnecting] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isConnected = Boolean(googleStatus?.connected);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  const handleConnect = useCallback(async () => {
    // Guard synchronously (before the await) so a rapid double-click can't open
    // a second popup and orphan the first poll interval.
    if (!organizationId || isConnecting) {
      return;
    }
    const popup = openOAuthPopup();
    if (!popup) {
      setStatus('error');
      return;
    }
    setIsConnecting(true);
    try {
      const url = await getUserAuthStartUrl(organizationId);
      popup.location.href = url;
    } catch {
      popup.close();
      setStatus('error');
      setIsConnecting(false);
      return;
    }
    // The popup lands back on the Integrations route after OAuth and closes
    // itself; poll for that so we refetch status the moment it finishes.
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
    }
    const timer = setInterval(() => {
      if (popup.closed) {
        // Clear this interval's own id, not whatever the ref currently holds.
        clearInterval(timer);
        pollTimerRef.current = null;
        setIsConnecting(false);
        void query.refetch();
      }
    }, 500);
    pollTimerRef.current = timer;
  }, [organizationId, query, isConnecting]);

  const handleGrantAccess = useCallback(async () => {
    if (!organizationId || !fileId) {
      return;
    }
    setStatus('opening-picker');
    const outcome = await openPickerForFile(organizationId, fileId);
    switch (outcome) {
      case 'granted':
        setStatus('granted');
        break;
      case 'picked-different':
        setStatus('picked-different');
        break;
      case 'reconnect-required':
        setStatus('reconnect-required');
        break;
      case 'error':
        setStatus('error');
        break;
      case 'cancelled':
        setStatus('idle');
        break;
    }
  }, [organizationId, fileId, openPickerForFile]);

  if (!fileId) {
    return (
      <Page>
        <Card>
          <H2>Something's missing</H2>
          <Text>This link is missing the file to grant access to. Head back to Slack and use the link Flora sent.</Text>
        </Card>
      </Page>
    );
  }

  if (status === 'granted') {
    return (
      <Page>
        <Card>
          <SuccessIcon icon="material-symbols:check-circle-outline" width={40} height={40} />
          <H2>Access granted</H2>
          <Text>Flora now has access to this file. Head back to Slack and ask again.</Text>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <H2>Give Flora access to this file</H2>

        {isConnected ? (
          <Text>Grant access by picking the file in Google's file picker — Flora can then read and update it.</Text>
        ) : (
          <Text>First connect your Google account, then pick the file to let Flora read and update it.</Text>
        )}

        {status === 'picked-different' && (
          <Hint>
            <Text size="small" weight="medium">
              That's a different file than the one Flora needs — pick the file in Google's file picker and try again.
            </Text>
          </Hint>
        )}

        {status === 'reconnect-required' && (
          <Hint>
            <Text size="small" weight="medium">
              Your Google connection needs to be refreshed.{' '}
              <IntegrationsLink to={INTEGRATIONS_PAGE_PATH}>Manage integrations</IntegrationsLink>
            </Text>
          </Hint>
        )}

        {status === 'error' && (
          <Hint>
            <Text size="small" weight="medium">
              Something went wrong opening the file picker. Please try again.
            </Text>
          </Hint>
        )}

        <Actions>
          {isConnected ? (
            <Button
              variant="primary"
              onClick={() => void handleGrantAccess()}
              loading={status === 'opening-picker'}
              radius="xl"
            >
              <Text weight="medium">Grant access</Text>
            </Button>
          ) : (
            <Button variant="primary" onClick={() => void handleConnect()} loading={isConnecting} radius="xl">
              <Text weight="medium">Connect Google account</Text>
            </Button>
          )}
        </Actions>
      </Card>
    </Page>
  );
}

const Page = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 48px 24px;
  width: 100%;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 520px;
  width: 100%;
  padding: 32px;
  border: 1px solid var(--sky-lighter);
  border-radius: 12px;
  background: var(--base-white);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const SuccessIcon = styled(Icon)`
  color: var(--green-base);
`;
